import { VpButton, VpFormControl, VpFormLabel, VpIcon, VpIconButton, VpTextarea } from '@vtmn-play/react'
import Modal from 'components/common/mygame/modal/modal'
import { useTranslation } from 'react-i18next'
import { useContext, useState } from 'react'
import { HomeContext } from '../../state/home-context'
import { useApiPutDirectorMessage } from 'core/api/director/useApiPutDirectorMessage'
import { useApiGetDirectorMessage } from 'core/api/director/useApiGetDirectorMessage'
import { useApiDeleteDirectorMessage } from 'core/api/director/useApiDeleteDirectorMessage'
import { useUserHasRoles } from 'core/hooks/useUserHasRoles'
import { ROLES } from 'core/constants/roles'
import { HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../../analytics/use-homepage-analytics'

const MAX_LENGTH = 250

const isValid = (value: string) => {
  return value?.length <= MAX_LENGTH
}

const StoreDirectorMessageEdition = () => {
  const { t } = useTranslation()
  const trackHomepageEvent = useHomepageAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState<string>(undefined)
  const [validationError, setValidationError] = useState<boolean>(undefined)
  const { storeId } = useContext(HomeContext)
  const userHasRightToEdit = useUserHasRoles([ROLES.ROLE_ADMIN, ROLES.ROLE_STORE_MANAGER])

  const { data } = useApiGetDirectorMessage(storeId, { enabled: !!storeId })
  const { mutate } = useApiPutDirectorMessage(storeId)
  const { mutate: mutateDelete } = useApiDeleteDirectorMessage(storeId)

  const confirm = () => {
    if (message && message !== data) {
      mutate(message)
      trackHomepageEvent(HOMEPAGE_EVENTS.DIRECTOR_MESSAGE_UPDATED)
    }
    if (!message && data) {
      mutateDelete()
      trackHomepageEvent(HOMEPAGE_EVENTS.DIRECTOR_MESSAGE_UPDATED)
    }
    setIsOpen(false)
  }

  const handleChange = (value: string) => {
    setMessage(value.trim())
    setValidationError(!isValid(value.trim()))
  }

  const open = () => {
    trackHomepageEvent(HOMEPAGE_EVENTS.CLICK_DIRECTOR_MESSAGE_EDIT)
    setMessage(data)
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setValidationError(undefined)
  }

  const footer = (
    <>
      <VpButton variant="secondary" variantStyle="regular" size="medium" onClick={close}>
        {t('general.homepage.store-director-message.edition.cancel')}
      </VpButton>
      <VpButton variant="primary" variantStyle="regular" size="medium" disabled={validationError} onClick={confirm}>
        {t('general.homepage.store-director-message.edition.confirm')}
      </VpButton>
    </>
  )

  return (
    userHasRightToEdit && (
      <>
        <VpIconButton variant="ghost" variantStyle="regular" size="medium" onClick={open}>
          <VpIcon {...{ name: 'feedback' }} />
        </VpIconButton>
        <Modal
          open={isOpen}
          onClose={close}
          closeOnClickOutside
          showCloseButton
          title={t('general.homepage.store-director-message.edition.title')}
          footer={footer}
        >
          <p className="mg-store-director-message-edit-content-subtitle vp-body-m">
            {t('general.homepage.store-director-message.edition.instructions')}
          </p>
          <div className="mg-store-director-message-edit-content">
            <VpFormControl>
              <VpFormLabel className="vp-body-s">
                {t('general.homepage.store-director-message.edition.message-label')}
              </VpFormLabel>
              <VpTextarea
                name="store-director-message"
                value={message ?? ''}
                onChange={event => {
                  handleChange(event.target.value)
                }}
                placeholder={t('general.homepage.store-director-message.edition.message-placeholder')}
                status={validationError ? 'error' : undefined}
              />
              <div className={`mg-store-director-message-edit-counter${validationError ? '__error' : ''} vp-body-s`}>
                {message?.length?.toString() || '0'}/{MAX_LENGTH}
              </div>
            </VpFormControl>
          </div>
        </Modal>
      </>
    )
  )
}

export default StoreDirectorMessageEdition
